import { createClient, type SupabaseClient } from '@supabase/supabase-js';

/**
 * Supabase is the identity provider (ADR-003-R). Only the publishable (anon) key ever reaches
 * the browser; every tenant boundary is enforced server-side by row-level security keyed on the
 * `tenant_id` claim, so shipping this key is safe.
 *
 * The client is created lazily: constructing it at module scope would make every module that
 * merely imports a type from here fail to load when the environment is not configured, which
 * breaks unit tests and the CI build for no benefit.
 */

let client: SupabaseClient | null = null;

function readRequiredEnv(name: 'VITE_SUPABASE_URL' | 'VITE_SUPABASE_PUBLISHABLE_KEY'): string {
  const value = import.meta.env[name];
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`Variável de ambiente ausente: ${name}. Copie .env.example para .env.local.`);
  }
  return value;
}

export function getSupabaseClient(): SupabaseClient {
  client ??= createClient(
    readRequiredEnv('VITE_SUPABASE_URL'),
    readRequiredEnv('VITE_SUPABASE_PUBLISHABLE_KEY'),
    {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        // The SPA has no server callback route yet; Sprint 2 owns the OAuth/magic-link flow.
        detectSessionInUrl: true,
        flowType: 'pkce',
      },
      global: {
        headers: { 'X-Client-Info': `instoque-web/${import.meta.env.VITE_APP_VERSION}` },
      },
    },
  );

  return client;
}

/** Test seam: drops the memoised client so a suite can install its own double. */
export function resetSupabaseClient(): void {
  client = null;
}
